// eslint-disable-next-line no-restricted-imports -- FIXME: This rule was recently enabled for the repo
import type { Variant } from '@mui/material/styles/createTypography';
import { useEffect, useRef, useState } from 'react';
import { Platform, View } from 'react-native';
import Script from 'next/script';
import { isNil } from 'es-toolkit';
import { useFormik } from 'formik';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import * as Yup from 'yup';

import type { LoginReturn } from '@savvy/deprecated-shared/contexts/jwt-context.context';
import type { ResultOf } from '@savvy/deprecated-shared/gql-compat';
import type { LoginData } from '@savvy/deprecated-shared/utils/savvy-auth';
import type { LoginFormik } from '@savvy/deprecated-shared/x-pages/savvy-auth/x-login-types';
import { SAVVY_ADVISORS_DOMAIN } from '@savvy/constants/savvy-advisors-domain';
import { apiHost } from '@savvy/deprecated-shared/api/api-host';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useIsSmallScreen } from '@savvy/deprecated-shared/hooks/use-is-small-screen';
import { useRouter } from '@savvy/deprecated-shared/hooks/use-router';
import { useTheme } from '@savvy/deprecated-shared/hooks/use-theme';
import { useLazyQueryWithErrorLogging } from '@savvy/deprecated-shared/hooks/useLazyQueryWithErrorLogging';
import { getReturnUrl } from '@savvy/deprecated-shared/utils/routing';
import {
  AuthFactor,
  detectedTimeZonePayload,
  errMsgToOtpChannel,
  getAuthHeader,
  getLoginContext,
  OtpChannel,
} from '@savvy/deprecated-shared/utils/savvy-auth';
import { XGuestGuard } from '@savvy/deprecated-shared/x-components/savvy-auth/x-guest-guard';
import { XCircularLoadingIndicator } from '@savvy/deprecated-shared/x-components/x-circular-loading-indicator';
import { XHead } from '@savvy/deprecated-shared/x-components/x-head';
import { XLink } from '@savvy/deprecated-shared/x-components/x-link';
import { XMuiThemeProvider } from '@savvy/deprecated-shared/x-components/x-mui-theme-provider';
import { toast } from '@savvy/deprecated-shared/x-components/x-toast';
import { XTypography } from '@savvy/deprecated-shared/x-components/x-typography';
import { XLoginForm } from '@savvy/deprecated-shared/x-pages/savvy-auth/x-login-form';
import { XLoginLayout } from '@savvy/deprecated-shared/x-pages/savvy-auth/x-login-layout';
import { XOtpLoginForm } from '@savvy/deprecated-shared/x-pages/savvy-auth/x-otp-login-form';

import { XLoginQuery } from './x-login.gql';

const MAX_STRING_LENGTH = 255;

type Content = {
  first_name?: string;
  body?: string;
};

function getHeader(content?: Content, otpChannel?: OtpChannel, client?: ResultOf<typeof XLoginQuery>['client']) {
  switch (otpChannel) {
    case OtpChannel.Email:
    case OtpChannel.Sms:
    case OtpChannel.Unknown:
      return 'Enter your security code';
    case undefined:
  }
  if (content?.first_name) {
    return `Welcome back, ${content.first_name}!`;
  }
  if (client) {
    if (client.household.coClient?.user.firstName) {
      return `Welcome back, ${client.user.firstName} and ${client.household.coClient.user.firstName}!`;
    }
    return `Welcome back, ${client.user.firstName}!`;
  }
  return 'Log in to Savvy';
}

function getBody(content: Content, otpChannel?: OtpChannel | null, returnURL?: string, variant?: Variant) {
  if (otpChannel === OtpChannel.Email) {
    return `Enter the 6-digit code we sent to your email.`;
  } else if (otpChannel === OtpChannel.Sms || otpChannel === OtpChannel.Unknown) {
    return `Enter the 6-digit code we sent to your phone number.`;
  } else if (content.body) {
    return content.body;
  } else if (returnURL && returnURL.includes('checkin')) {
    return `Please log in to start your check-in.`;
  } else if (returnURL?.startsWith('/oauth/authorize')) {
    return (
      <>
        {`Savvy staff without a Dashboard login? `}
        <XLink
          href={`/get-read-only-access?returnUrl=${encodeURIComponent(returnURL)}`}
          rel="noreferrer"
          target="_self"
          variant={variant}
        >
          Get read-only access
        </XLink>
      </>
    );
  }
  return (
    <>
      {`Don’t have a Savvy account? `}
      <XLink href="create-account" rel="noreferrer" target="_self" variant={variant}>
        Create an account
      </XLink>
    </>
  );
}

function XLoginInner() {
  const router = useRouter();
  const isReAuth = !!router.query['reAuth'];
  const [isLoadingContent, setIsLoadingContent] = useState(!!router.query['login_context']);
  const [content, setContent] = useState<Content>({});
  const { login, user } = useAuth();

  const [getClientData, { data: clientData, loading: clientDataLoading }] = useLazyQueryWithErrorLogging(XLoginQuery, {
    context: {
      headers: {
        authorization: getAuthHeader(router),
      },
    },
  });
  useEffect(() => {
    simpleServerTest();

    if (router.query['login_context']) {
      fetch(`${apiHost}/landing_page/decrypt?content=${encodeURIComponent(String(router.query['login_context']))}`)
        .then((response) => {
          // eslint-disable-next-line typescript/consistent-type-assertions -- FIXME: This rule was recently enabled for the repo
          return response.json() as Promise<Content>;
        })
        .then((responseJson) => {
          setContent(responseJson);
          setIsLoadingContent(false);
        })
        .catch(() => {
          setIsLoadingContent(false);
        });
    }
    if (getLoginContext(router)) {
      void getClientData();
      return;
    }
  }, []);
  return (
    <XLoginView
      clientData={clientData}
      content={content}
      isReAuth={isReAuth}
      isLoadingContent={isLoadingContent}
      clientDataLoading={clientDataLoading}
      returnUrl={getReturnUrl(router, user)}
      login={login}
    />
  );
}

export type XLoginViewProps = Readonly<{
  clientData?: ResultOf<typeof XLoginQuery>;
  content: Content;
  isReAuth?: boolean;
  isLoadingContent?: boolean;
  clientDataLoading?: boolean;
  returnUrl: string;
  login: (data: LoginData) => Promise<LoginReturn>;
}>;
export function XLoginView({
  clientData,
  content,
  isReAuth,
  isLoadingContent,
  clientDataLoading,
  returnUrl,
  login,
}: XLoginViewProps) {
  const theme = useTheme();
  const isSmallScreen = useIsSmallScreen();
  const isSubmittingRef = useRef(false);
  useEffect(() => {
    if (clientData?.client.user.email) {
      formik.setFieldValue('email', clientData.client.user.email);
    }
  }, [clientData]);

  const formik: LoginFormik = useFormik({
    initialValues: {
      email: '',
      password: '',
      submit: '',
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email('Must be a valid email')
        .max(MAX_STRING_LENGTH, `Email must be at most ${MAX_STRING_LENGTH} characters`)
        .test(
          'email-is-client',
          'This app is not configured for advisor accounts. Please log in with your client account instead.',
          (email) =>
            Platform.OS === 'web' || // Don't run this validation on web
            !email?.toLowerCase().includes(`@${SAVVY_ADVISORS_DOMAIN}`),
        )
        .required('Email is required'),
      password: Yup.string()
        .max(MAX_STRING_LENGTH, `Password must be at most ${MAX_STRING_LENGTH} characters`)
        .required('Password is required'),
      submit: Yup.string().nullable(),
    }),
    onSubmit: (values, helpers) => {
      if (isSubmittingRef.current) {
        return;
      }
      isSubmittingRef.current = true;
      login({
        type: AuthFactor.First,
        payload: {
          email: values.email,
          password: values.password,
          ...detectedTimeZonePayload(),
        },
      })
        .then(({ loginErr }) => {
          if (loginErr) {
            console.warn(loginErr);
            helpers.setStatus({ success: false });
            helpers.setErrors({ submit: loginErr });
            helpers.setSubmitting(false);
          }
        })
        .finally(() => {
          isSubmittingRef.current = false;
        });
    },
  });

  if (isLoadingContent || clientDataLoading) {
    return <XCircularLoadingIndicator />;
  }

  const otpChannel = errMsgToOtpChannel[formik.errors.submit ?? ''];
  const otpSent = !isNil(otpChannel);

  return (
    <View
      style={{
        marginHorizontal: isSmallScreen ? 24 : 0,
      }}
    >
      <XHead>
        <title>Savvy | Log in to Your Account</title>
        <meta
          name="description"
          content="Log in to view and manage tasks from your financial advisor, track your real-time net worth, and more."
        />
      </XHead>

      {typeof document !== 'undefined' ? <Script src="https://status.savvywealth.com/embed/script.js" /> : null}

      <XLoginLayout>
        <View style={{ flexGrow: 1 }}>
          <View>
            <XTypography
              variant={isSmallScreen ? 'h5' : 'h3'}
              color={theme.palette.text.charcoal}
              fontWeight="600"
              style={{ paddingBottom: 8 }}
            >
              {getHeader(content, otpChannel, clientData?.client)}
            </XTypography>
          </View>
          <View>
            <XTypography variant={isSmallScreen ? 'body2' : 'body1'} color="textSecondary" style={{ marginBottom: 40 }}>
              {getBody(content, otpChannel, returnUrl, isSmallScreen ? 'body2' : 'body1')}
            </XTypography>
          </View>
          {isReAuth && (
            <View style={{ marginBottom: 16 }}>
              <XTypography color={theme.palette.error.main} variant="caption">
                Your session has expired, please log in again.
              </XTypography>
            </View>
          )}
          {otpSent ? <XOtpLoginForm formik={formik} /> : <XLoginForm clientFrag={clientData?.client} formik={formik} />}
        </View>
      </XLoginLayout>
    </View>
  );
}

const simpleServerTest = () => {
  if (process.env['NODE_ENV'] === 'development') {
    fetch(`${apiHost}/test`)
      .then((response) => {
        // eslint-disable-next-line typescript/consistent-type-assertions -- FIXME: This rule was recently enabled for the repo
        return response.json() as Promise<{ exception?: string }>;
      })
      .then((responseJson) => {
        if (responseJson.exception?.includes('ActiveRecord::PendingMigrationError')) {
          toast.error('Pending Migration Error');
        }
      })
      .catch(() => {
        // `fetch()` rejects on network failure with a TypeError (no `response`
        // property), so any failure here is a server/network issue.
        toast.error('Server Error');
      });
  }
};

const XLoginWithLDConsumer = withLDConsumer()(XLoginInner);

export function XLogin() {
  return (
    <XMuiThemeProvider>
      <XGuestGuard>
        <XLoginWithLDConsumer />
      </XGuestGuard>
    </XMuiThemeProvider>
  );
}
